import { SCENES } from '../../data/scenes'
import { WP } from '../../data/waypoints'

/**
 * Single source of truth for the instrumentable surface of the site (spec §5.1).
 *
 * - Routes/sitemap/prerender read `zones[].route` (scripts/prerender.mjs via seo.config.ts).
 * - The focus engine (Phase 3) reads `zones[].bounds` + `readableZoom`.
 * - Mobile IntersectionObserver bindings read `zones[].linearSectionId`.
 * - The CI parity test (Phase 4) asserts a binding exists for every entry here.
 *
 * Geometry is DERIVED from data/waypoints.ts (never duplicated): WP x,y are the
 * camera-fit CENTER of each region (types.ts Waypoint), so bounds are converted
 * to top-left rects here. Zone order = WP order = narrative order.
 */

export interface Rect { x: number; y: number; w: number; h: number } // world coords, top-left
export interface Zone {
  id: ZoneId
  name: string
  bounds: Rect
  readableZoom: number
  route: string
  linearSectionId: string
}
export interface Draggable { id: string; zoneId: ZoneId | 'hud'; kind: 'card' | 'node' | 'toy' }
export interface MiniGame { id: string; zoneId: ZoneId; milestones: string[] }
export interface EasterEgg { id: string; zoneId: ZoneId | 'hud'; trigger: 'click' | 'zoom' | 'sequence' | 'idle' }
export interface TourStep { id: ZoneId; index: number; zoneId: ZoneId; durationMs: number }

export type ZoneId =
  | 'home' | 'pain' | 'timing' | 'positioning' | 'product' | 'architecture'
  | 'workflow' | 'platform-model' | 'differentiation' | 'licensing' | 'action'

/** Every discrete HUD/chrome control (engine/canvas.ts onOverlayClick + input keys + mobile menu). */
export type HudControl =
  | 'wordmark-home' | 'nav-product' | 'nav-architecture' | 'nav-modules' | 'nav-licensing'
  | 'nav-github' | 'nav-docs' | 'theme-toggle' | 'login'
  | 'zoom-in' | 'zoom-out' | 'zoom-reset' | 'zoom-fit'
  | 'tour-toggle' | 'beat-prev' | 'beat-next' | 'srnav-jump' | 'menu'

/**
 * lodTier's 'full' threshold (engine/math.ts): detail content is visible from s >= 0.62,
 * and flyToWaypoint's fit floor is the same 0.62 — the natural "readable" zoom.
 */
const READABLE_ZOOM = 0.62

/** Per-zone route + linear-section registry. Scene ids are the repo's designated slugs
 *  (data/scenes.ts:4-5); routes are the approved SEO slugs from the Phase D zone table. */
const ZONE_TABLE: Record<ZoneId, { route: string; linearSectionId: string }> = {
  home: { route: '/', linearSectionId: 'home' },
  pain: { route: '/problem', linearSectionId: 'pain' },
  timing: { route: '/why-now', linearSectionId: 'timing' },
  positioning: { route: '/foundation', linearSectionId: 'positioning' },
  product: { route: '/studio', linearSectionId: 'product' },
  architecture: { route: '/architecture', linearSectionId: 'architecture' },
  workflow: { route: '/developer-loop', linearSectionId: 'workflow' },
  'platform-model': { route: '/modules', linearSectionId: 'platform-model' },
  differentiation: { route: '/live-modules', linearSectionId: 'live-modules' },
  licensing: { route: '/licensing', linearSectionId: 'licensing' },
  action: { route: '/come-build', linearSectionId: 'action' },
}

function zoneFromScene(id: ZoneId): Zone {
  const scene = SCENES.find((s) => s.id === id)
  if (!scene) throw new Error(`interaction-manifest: no scene with id "${id}"`)
  const wp = WP[scene.wpIndex]
  return {
    id,
    name: scene.title,
    bounds: { x: wp.x - wp.w / 2, y: wp.y - wp.h / 2, w: wp.w, h: wp.h },
    readableZoom: READABLE_ZOOM,
    route: ZONE_TABLE[id].route,
    linearSectionId: ZONE_TABLE[id].linearSectionId,
  }
}

export const MANIFEST: {
  zones: Zone[]
  draggables: Draggable[]
  games: MiniGame[]
  eggs: EasterEgg[]
  tour: TourStep[]
  hud: HudControl[]
} = {
  zones: (Object.keys(ZONE_TABLE) as ZoneId[]).map(zoneFromScene),

  // Everything matching input.ts DRAGGABLE ('[data-node-id]:not([data-anchor])'), grouped
  // by producing component, plus the ArmTwin physics block (its own drag system).
  draggables: [
    ...(['sensors', 'perception', 'planner', 'control', 'deploy'] as const)
      .map((id): Draggable => ({ id, zoneId: 'home', kind: 'node' })),
    ...(['wn_jet', 'wn_esp', 'wn_cust', 'wn_pol', 'wn_mod', 'wn_ctl', 'wn_beh'] as const)
      .map((id): Draggable => ({ id, zoneId: 'timing', kind: 'node' })),
    ...(['p_model', 'p_program', 'p_sim', 'p_deploy', 'p_modify'] as const)
      .map((id): Draggable => ({ id, zoneId: 'product', kind: 'node' })),
    ...(['pcard_graph', 'pcard_scope', 'pcard_target'] as const)
      .map((id): Draggable => ({ id, zoneId: 'product', kind: 'card' })),
    { id: 'pr_deck', zoneId: 'pain', kind: 'card' },
    ...(['pr_lane_0', 'pr_lane_1', 'pr_lane_2'] as const)
      .map((id): Draggable => ({ id, zoneId: 'pain', kind: 'card' })),
    ...(['rt_frameworks', 'rt_shinro'] as const)
      .map((id): Draggable => ({ id, zoneId: 'architecture', kind: 'card' })),
    ...(['dl_step_01', 'dl_step_02', 'dl_step_03', 'dl_step_04'] as const)
      .map((id): Draggable => ({ id, zoneId: 'workflow', kind: 'card' })),
    ...(['tc_develop', 'tc_share', 'tc_run'] as const)
      .map((id): Draggable => ({ id, zoneId: 'platform-model', kind: 'card' })),
    ...(['lic_studio', 'lic_modules', 'lic_cake'] as const)
      .map((id): Draggable => ({ id, zoneId: 'licensing', kind: 'card' })),
    ...(['ks_l4', 'ks_l3', 'ks_l2', 'ks_l1', 'ks_kernel', 'ks_hw'] as const)
      .map((id): Draggable => ({ id, zoneId: 'positioning', kind: 'card' })),
    { id: 'armtwin-block', zoneId: 'differentiation', kind: 'toy' },
  ],

  // ArmTwin is the only game-like interactive (Phase D). KernelStack / MiniNodeGraph /
  // TargetPicker are toys -> component_interact (C1), not G* events.
  games: [{
    id: 'armtwin',
    zoneId: 'differentiation',
    milestones: [
      'first_block_drag', 'task_complete', 'module_camera', 'module_force',
      'module_base', 'planner_swap', 'gripper_drop', 'block_offworld',
    ],
  }],

  // The logo decuple-click history modal is the only real easter egg in the build.
  eggs: [{ id: 'history-modal', zoneId: 'hud', trigger: 'sequence' }],

  // Tour order = WP order. durationMs = wall time per stop in the non-reduced path:
  // dwell timer 3400ms (runs concurrently with the 900ms flight) + 720ms travel beat;
  // final stop is 900ms flight + 1400ms stop timer (engine/tour.ts).
  tour: (Object.keys(ZONE_TABLE) as ZoneId[]).map((id, i, all): TourStep => ({
    id, index: i, zoneId: id, durationMs: i === all.length - 1 ? 2300 : 4120,
  })),

  hud: [
    'wordmark-home', 'nav-product', 'nav-architecture', 'nav-modules', 'nav-licensing',
    'nav-github', 'nav-docs', 'theme-toggle', 'login',
    'zoom-in', 'zoom-out', 'zoom-reset', 'zoom-fit',
    'tour-toggle', 'beat-prev', 'beat-next', 'srnav-jump', 'menu',
  ],
}

/** Route lookup with trailing-slash normalization ('/studio/' matches '/studio'). */
export function zoneForPath(pathname: string): Zone | null {
  const path = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  return MANIFEST.zones.find((z) => z.route === path) ?? null
}

/** WP index for a zone (camera navigation), from the scene registry. */
export function wpIndexForZone(id: ZoneId): number {
  const scene = SCENES.find((s) => s.id === id)
  if (!scene) throw new Error(`interaction-manifest: no scene with id "${id}"`)
  return scene.wpIndex
}

/** Reverse lookup: which zone a WP index belongs to (region-change -> head sync). */
export function zoneIdForWpIndex(wpIndex: number): ZoneId | null {
  const scene = SCENES.find((s) => s.wpIndex === wpIndex)
  return scene ? (scene.id as ZoneId) : null
}
